import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ApiPaths, api } from "@/lib/api";
import type { Session } from "@/lib/types";

/** Thresholds in minutes BEFORE the cap when we change tone. */
const WARN_AT_MINUTES_BEFORE = 30;
const URGENT_AT_MINUTES_BEFORE = 5;

type Props = {
  session: Pick<
    Session,
    "id" | "started_at" | "conference_cap_minutes" | "status"
  >;
};

/** Phase 2F-bill-e — operator-side conference cap countdown.
 *
 * The investor pricing model caps every paid conference at 180 min
 * (org's plan.conference_cap_minutes). At 30 min remaining the banner
 * flips to warning yellow; at 5 min it goes urgent red and offers the
 * "Start follow-up conference" handoff. The server-side sweep in
 * `apps/sessions/tasks.py::auto_close_overcap_sessions` is the
 * backstop — if the user keeps the tab open past zero the next sweep
 * (≤60s) closes it from the server. We render nothing when the cap is
 * absent (free plan with no session yet, enterprise unlimited, etc.).
 */
export function ConferenceCapBanner({ session }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  const followUp = useMutation({
    mutationFn: async () => {
      // End the current session, then create a fresh one that the
      // operator can re-invite the field user to. We deliberately
      // don't auto-copy participants — re-inviting is the explicit
      // signal that this is a new, separately-billed conference.
      await api.post(ApiPaths.sessionEnd(session.id));
      const res = await api.post<Session>(ApiPaths.sessions, {});
      return res.data;
    },
    onSuccess: (newSess) => {
      void qc.invalidateQueries({ queryKey: ["sessions"] });
      navigate(`/sessions/${newSess.id}`, { replace: true });
    },
  });

  // Bail-outs.
  if (!session.conference_cap_minutes) return null;
  if (!session.started_at) return null;
  // Only while a conference is actually running. Reported 2026-09-24:
  // a beneficiary opened their link a day early and stayed 38 seconds,
  // which stamped `started_at`; with no status gate the banner then
  // measured `now - started_at` = 667 minutes against a 180-minute cap
  // and announced "cap reached, server will close shortly" on a session
  // scheduled for the NEXT day that had not started.
  //
  // These are the same statuses the server-side sweep
  // (`auto_close_overcap_sessions`) acts on, which is the authority on
  // what counts as a running conference — the banner should not claim
  // something the backstop would not do.
  if (!["open", "recording", "paused"].includes(session.status)) return null;

  const startedMs = new Date(session.started_at).getTime();
  const elapsedMin = (now - startedMs) / 60_000;
  const remainingMin = session.conference_cap_minutes - elapsedMin;

  // Don't render until the warning window.
  if (remainingMin > WARN_AT_MINUTES_BEFORE) return null;

  const urgent = remainingMin <= URGENT_AT_MINUTES_BEFORE;
  const overCap = remainingMin <= 0;

  const tone = overCap
    ? "border-cvp-danger bg-cvp-danger/15 text-cvp-danger"
    : urgent
      ? "border-cvp-danger bg-cvp-danger/10 text-cvp-danger"
      : "border-amber-500 bg-amber-500/10 text-amber-200";

  const minutesLabel = Math.max(0, Math.ceil(remainingMin));
  const secondsLabel = urgent
    ? Math.max(0, Math.floor((remainingMin - Math.floor(remainingMin)) * 60))
        .toString()
        .padStart(2, "0")
    : null;

  return (
    <div
      data-testid="conference-cap-banner"
      className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-xs ${tone}`}
    >
      <div>
        <div className="font-semibold uppercase tracking-wide">
          {overCap
            ? t("ui.conferenceCapBanner.capReached")
            : urgent
              ? t("ui.conferenceCapBanner.endingIn", { minutes: minutesLabel, seconds: secondsLabel })
              : t("ui.conferenceCapBanner.endsIn", { minutes: minutesLabel })}
        </div>
        <div className="mt-0.5 text-[11px] opacity-80">
          {overCap
            ? t("ui.conferenceCapBanner.overCapHint")
            : t("ui.conferenceCapBanner.capHint", { minutes: session.conference_cap_minutes })}
        </div>
      </div>
      <button
        type="button"
        className="btn-ghost shrink-0 text-xs"
        onClick={() => followUp.mutate()}
        disabled={followUp.isPending}
      >
        {followUp.isPending ? t("ui.conferenceCapBanner.starting") : t("ui.conferenceCapBanner.startFollowUp")}
      </button>
    </div>
  );
}
